import React, { useCallback } from 'react';

import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { Control } from '@vlabs/uikit';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './SmallFaceCard.module.sass';
import { useGetFace } from '@vlabs/api/luna-api/client';
import { buildTestid } from '../../../lib/utils';
import type { BaseFaceCardProps } from '../base-face-card/BaseFaceCard';
import { BaseFaceCard } from '../base-face-card/BaseFaceCard';

export interface SmallFaceCardProps extends Omit<BaseFaceCardProps, 'size' | 'src'> {
  selectable?: boolean;
  isSelected?: boolean;
  onSelect?: (next: boolean, externalId?: string) => void;
  rightButton?: React.JSX.Element;
}

const displayName = 'SmallFaceCard';
const testid = buildTestid(displayName);

export function SmallFaceCard({
  faceId,
  selectable = undefined,
  isSelected = undefined,
  onSelect = undefined,
  rightButton = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: SmallFaceCardProps): React.JSX.Element {
  const { t } = useTranslation();
  // @ts-expect-error проверка FaceId дальше
  const getFace = useGetFace(faceId, { targets: ['face_id', 'create_time', 'avatar'] }, {
    query: {
      enabled: !!faceId,
    },
  });
  const face = getFace.data;

  const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onSelect?.(e.target.checked, face?.face_id);
  }, [onSelect, face?.face_id]);

  return (
    <div
      className={cn(
        st.Root,
        { [(st.Root_Selected as string)]: isSelected },
      )}
      {...testid(faceId, dataTestId)}
    >
      <BaseFaceCard
        faceId={face?.face_id}
        size="small"
        src={face?.avatar ? `/api/lp5${face.avatar}` : undefined}
        {...props}
      />
      {!selectable && (
        <div
          className={st.CraeteTimeData}
          {...testid(faceId, 'CreateTime', dataTestId)}
        >
          <span
            className={st.CreateTimeLabel}
            {...testid(faceId, 'CreateTime', 'Label', dataTestId)}
          >
            {t('monokit:face card.face.created')}
          </span>
          <span
            className={st.CreateTimeValue}
            {...testid(faceId, 'CreateTime', 'Value', dataTestId)}
          >
            {parseISOToLocale(face?.create_time)}
          </span>
        </div>
      )}
      {!!selectable && !!face?.face_id && (
        <div
          className={st.SelectableData}
          {...testid(faceId, 'Selectable', dataTestId)}
        >
          <Control.Checkbox
            checked={isSelected}
            className={st.SmallFaceCardCheckbox}
            {...testid(faceId, 'Selectable', 'Checkbox', dataTestId)}
            onChange={handleCheckboxChange}
          />
          <span
            className={st.FaceIdLabel}
            {...testid(faceId, 'Selectable', 'FaceId', dataTestId)}
          >
            <TruncatedText
              hasTruncation
              lineClamp={2}
              tooltipContent={face?.face_id}
            >
              {face?.face_id}
            </TruncatedText>
          </span>
          {!!rightButton && (
            <div
              className={st.RightButton}
              {...testid(faceId, 'RightButton', dataTestId)}
            >
              {rightButton}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
